import { v4 as uuid } from 'uuid';
import { deepCopyObject } from '@/appdata/globals';
import type { SwadeModificationInterface } from '@/types/items';

/**
 * Every nested modification a configuration carries — an option in `element`, a carried entry in
 * `carry`, and theirs in turn — visited depth first. A nested entry is a modification only when it
 * carries a `configuration` of its own; chips and bare strings are data and are skipped.
 */
const forEachNestedModification = (configuration: unknown, visit: (node: { uuid?: unknown, configuration?: unknown }) => void): void => {
    const lists = configuration as { element?: unknown, carry?: unknown } | null;
    if (!lists || typeof lists !== 'object') return;
    for (const list of [lists.element, lists.carry]) {
        if (!Array.isArray(list)) continue;
        for (const entry of list) {
            if (!entry || typeof entry !== 'object' || !('configuration' in entry)) continue;
            const node = entry as { uuid?: unknown, configuration?: unknown };
            visit(node);
            forEachNestedModification(node.configuration, visit);
        }
    }
};

/**
 * A modification's clone: the same configuration, byte for byte, under a new identity. Every uuid
 * that IDENTIFIES a modification is fresh — the root's, and a nested option's or carried entry's
 * when it has one (choice-set records resolve their option by that uuid, so two sets must not share
 * it). A uuid that merely REFERENCES something — an edge to grant, an ability chip, a container
 * target — is data and stays as authored.
 */
export function cloneModification(source: SwadeModificationInterface): SwadeModificationInterface {
    const clone = deepCopyObject(source);
    clone.uuid = uuid();
    forEachNestedModification(clone.configuration, (node) => {
        if (typeof node.uuid === 'string') node.uuid = uuid();
    });
    return clone;
}

/**
 * A modification without the licensed text of its effects (owner, 2026-09-25): a copy whose
 * `configuration.summary` — the set's, each rung's or option's, each carried entry's, at any depth —
 * is null wherever the author wrote one. What a clone of an element, a world or a setting rule the
 * user does not own carries; everything else survives byte for byte, the identity included.
 */
export function withoutEffectSummaries(source: SwadeModificationInterface): SwadeModificationInterface {
    const clone = deepCopyObject(source);
    const blank = (configuration: unknown): void => {
        if (configuration && typeof configuration === 'object' && 'summary' in configuration) {
            (configuration as { summary: unknown }).summary = null;
        }
    };
    blank(clone.configuration);
    forEachNestedModification(clone.configuration, (node) => blank(node.configuration));
    return clone;
}
